import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  Chip,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import dayjs from "dayjs";
import "dayjs/locale/vi";
import PaymentProfileModal from "./PaymentProfileModal";
import { getToken } from "services/auth.service";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import { formatAmount, formatCurrencyWithUnit, formatDate } from "utils/formatters";
import { AppButton } from "components/common/buttons";

const PaymentProfileTable = ({
  contractId,
  contractNumber,
  contractName,
  contractItems = [],
  contractData = null, // Thêm contractData để lấy VAT rate
  onPaymentProfileSelect,
  refreshKey = 0, // Thêm refreshKey prop
}) => {
  const [paymentProfiles, setPaymentProfiles] = useState([]);
  const [contractPayments, setContractPayments] = useState([]); // Tất cả thanh toán của hợp đồng
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Permission Snackbar state
  const [permissionSnackbar, setPermissionSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  const loadPaymentProfiles = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/PaymentProfile/contract/${contractId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setPaymentProfiles(result.data || []);
      } else {
        throw new Error("Failed to load payment profiles");
      }
    } catch (error) {
      console.error("Error loading payment profiles:", error);
      showSnackbar("Lỗi tải danh sách kỳ thanh toán", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadContractPayments = async () => {
    if (!contractId) return;

    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/ContractPayment/contract/${contractId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setContractPayments(result.data || []);
      } else {
        console.warn("Could not load contract payments");
        setContractPayments([]);
      }
    } catch (error) {
      console.error("Error loading contract payments:", error);
      setContractPayments([]);
    }
  };

  useEffect(() => {
    if (contractId) {
      loadPaymentProfiles();
      loadContractPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, refreshKey]); // Thêm refreshKey vào dependency

  // Tính thành tiền cho một payment
  const calculatePaymentAmount = (payment) => {
    // Ưu tiên dùng PaymentAmount từ database
    if (payment.paymentAmount && payment.paymentAmount > 0) {
      return Math.round(payment.paymentAmount);
    }

    // Fallback: tính toán nếu không có PaymentAmount từ DB
    const item = contractItems.find(
      (item) => item.itemId === payment.contractItemId
    );
    if (!item) return 0;

    return Math.round(item.unitPrice * payment.paymentQuantity);
  };

  // Tính tổng trước thuế cho một payment profile
  const getTotalBeforeTaxForProfile = (paymentProfileId) => {
    const profilePayments = contractPayments.filter(
      (payment) => payment.paymentProfileId === paymentProfileId
    );

    return Math.round(
      profilePayments.reduce((total, payment) => {
        return total + calculatePaymentAmount(payment);
      }, 0)
    );
  };

  // Tính tổng sau thuế cho một payment profile
  const getTotalAfterTaxForProfile = (paymentProfile) => {
    const beforeTax = getTotalBeforeTaxForProfile(
      paymentProfile.paymentProfileId
    );
    const vatAmount = beforeTax * (paymentProfile.vat / 100);
    return Math.round(beforeTax + vatAmount);
  };

  // Tính tổng giá trị hợp đồng trước thuế
  const getContractTotalBeforeTax = () => {

    // Thử cách 1: Tính tổng tất cả items có contractAmount > 0 (bất kể có children hay không)
    const total = Math.round(
      contractItems.reduce((sum, item) => {
        if (item.contractAmount && item.contractAmount > 0) {
          return sum + item.contractAmount;
        }
        return sum;
      }, 0)
    );

    return total;
  }; // Tính tổng giá trị hợp đồng sau thuế (VAT của hợp đồng)
  const getContractTotalAfterTax = () => {
    const beforeTax = getContractTotalBeforeTax();
    // Sử dụng VAT từ contractData giống như ContractItemsTable
    const vatRate = (contractData?.vat || 8) / 100; // Default 8% if not specified
    const vatAmount = beforeTax * vatRate;
    return Math.round(beforeTax + vatAmount);
  };

  // Tính tổng đã thanh toán trước thuế (tất cả các kỳ)
  const getTotalPaidBeforeTax = () => {
    return Math.round(
      contractPayments.reduce((total, payment) => {
        return total + calculatePaymentAmount(payment);
      }, 0)
    );
  };

  // Tính tổng đã thanh toán sau thuế (cộng tổng của từng kỳ đã tính VAT)
  const getTotalPaidAfterTax = () => {
    return Math.round(
      paymentProfiles.reduce((total, profile) => {
        return total + getTotalAfterTaxForProfile(profile);
      }, 0)
    );
  };

  // Handle add payment profile with permission check
  const handleAddPaymentProfile = async () => {
    try {
      const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_CREATE);
      if (result.hasPermission) {
        setEditingProfile(null);
        setShowModal(true);
      } else {
        setPermissionSnackbar({
          open: true,
          message: "Bạn không có quyền tạo kỳ thanh toán",
          severity: "warning",
        });
      }
    } catch (error) {
      console.error("Error checking permission:", error);
      setPermissionSnackbar({
        open: true,
        message: "Lỗi kiểm tra quyền truy cập",
        severity: "error",
      });
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSaveProfile = async (profileData, profileId = null) => {
    try {
      const token = getToken();
      const url = profileId
        ? `${process.env.REACT_APP_API_URL}/api/PaymentProfile/${profileId}`
        : `${process.env.REACT_APP_API_URL}/api/PaymentProfile`;

      const method = profileId ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(profileData),
      });

      if (response.ok) {
        await loadPaymentProfiles();
        showSnackbar(
          profileId
            ? "Cập nhật kỳ thanh toán thành công"
            : "Thêm kỳ thanh toán thành công"
        );
      } else {
        const error = await response.json();
        throw new Error(error.message || "Lỗi khi lưu kỳ thanh toán");
      }
    } catch (error) {
      console.error("Error saving payment profile:", error);
      showSnackbar(error.message || "Lỗi khi lưu kỳ thanh toán", "error");
      throw error;
    }
  };

  const handleDeleteProfile = async (profileId) => {
    // Không dùng confirm nữa, thay bằng modal
    // if (!window.confirm("Bạn có chắc chắn muốn xóa kỳ thanh toán này?")) return;

    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/PaymentProfile/${profileId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        await loadPaymentProfiles();
        showSnackbar("Xóa kỳ thanh toán thành công");
      } else {
        const error = await response.json();
        throw new Error(error.message || "Lỗi khi xóa kỳ thanh toán");
      }
    } catch (error) {
      console.error("Error deleting payment profile:", error);
      showSnackbar(error.message || "Lỗi khi xóa kỳ thanh toán", "error");
    }
  };

  // Hàm mở dialog xác nhận xóa
  const handleDeleteClick = (profile) => {
    setProfileToDelete(profile);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // Hàm xác nhận xóa
  const handleConfirmDelete = async () => {
    if (profileToDelete) {
      await handleDeleteProfile(profileToDelete.paymentProfileId);
      setDeleteDialogOpen(false);
      setProfileToDelete(null);
    }
  };

  // Hàm hủy xóa
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setProfileToDelete(null);
  };

  const handleMenuClick = (event, profile) => {
    event.stopPropagation(); // Ngăn event bubble up để không trigger row click
    setAnchorEl(event.currentTarget);
    setSelectedProfile(profile);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProfile(null);
  };

  const handleEdit = async () => {
    try {
      const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_UPDATE);
      if (result.hasPermission) {
        setEditingProfile(selectedProfile);
        setShowModal(true);
        handleMenuClose();
      } else {
        setPermissionSnackbar({
          open: true,
          message: "Bạn không có quyền chỉnh sửa kỳ thanh toán",
          severity: "warning",
        });
        handleMenuClose();
      }
    } catch (error) {
      console.error("Error checking permission:", error);
      setPermissionSnackbar({
        open: true,
        message: "Lỗi kiểm tra quyền truy cập",
        severity: "error",
      });
      handleMenuClose();
    }
  };

  const handleDelete = async () => {
    try {
      const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_DELETE);
      if (result.hasPermission) {
        if (selectedProfile) {
          handleDeleteClick(selectedProfile);
        }
        // handleMenuClose() đã được gọi trong handleDeleteClick
      } else {
        setPermissionSnackbar({
          open: true,
          message: "Bạn không có quyền xóa kỳ thanh toán",
          severity: "warning",
        });
        handleMenuClose();
      }
    } catch (error) {
      console.error("Error checking permission:", error);
      setPermissionSnackbar({
        open: true,
        message: "Lỗi kiểm tra quyền truy cập",
        severity: "error",
      });
      handleMenuClose();
    }
  };

  const columns = [
    {
      accessorKey: "paymentName",
      header: "Tên kỳ thanh toán",
      size: 180,
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {cell.getValue()}
        </Typography>
      ),
    },
    {
      accessorKey: "paymentDate",
      header: "Ngày thanh toán",
      size: 120,
      Cell: ({ cell }) => {
        const date = dayjs(cell.getValue());
        return (
          // <Typography variant="body2">{date.format("DD/MM/YYYY")}</Typography>
          <Typography variant="body2">{formatDate(cell.getValue())}</Typography>
        );
      },
    },
    {
      accessorKey: "vat",
      header: "VAT",
      size: 70,
      Cell: ({ cell }) => (
        <Chip
          label={`${cell.getValue()}%`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{
            fontWeight: 500,
            border: "none",
            backgroundColor: "#e3f2fd",
            color: "#1976d2",
          }}
        />
      ),
    },
    {
      id: "totalBeforeTax",
      header: "Tổng trước thuế",
      size: 140,
      Cell: ({ row }) => {
        const total = getTotalBeforeTaxForProfile(
          row.original.paymentProfileId
        );
        return (
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatAmount(total)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              VNĐ
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "totalAfterTax",
      header: "Tổng sau thuế",
      size: 140,
      Cell: ({ row }) => {
        const total = getTotalAfterTaxForProfile(row.original);
        return (
          <Box sx={{ textAlign: "right" }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#1976d2",
              }}
            >
              {formatAmount(total)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              VNĐ
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "notes",
      header: "Ghi chú",
      size: 150,
      Cell: ({ cell }) => {
        const notes = cell.getValue();
        return (
          <Typography variant="body2" color="text.secondary">
            {notes || "—"}
          </Typography>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Ngày tạo",
      size: 120,
      Cell: ({ cell }) => {
        const date = dayjs(cell.getValue());
        return (
          <Typography variant="body2" color="text.secondary">
            {/* {date.format("DD/MM/YYYY")} */}
            {formatDate(cell.getValue())}
          </Typography>
        );
      },
    },
    {
      id: "actions",
      header: "",
      size: 60,
      enableSorting: false,
      Cell: ({ row }) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuClick(e, row.original)}
          sx={{ color: "#666" }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
          >
            Kỳ thanh toán
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: "Inter, sans-serif",
              color: "text.secondary",
              fontStyle: "italic",
            }}
          >
            💡 Click vào dòng để xem chi tiết thanh toán
          </Typography>
        </Box>
        <AppButton
          startIcon={<AddIcon />}
          variant="contained"
          onClick={handleAddPaymentProfile}
        >
          Thêm kỳ thanh toán
        </AppButton>
      </Box>

      <Paper
        sx={{
          border: "1px solid #e0e0e0",
          boxShadow: "none",
          elevation: 0,
        }}
      >
        <MaterialReactTable
          columns={columns}
          data={paymentProfiles}
          state={{ isLoading: loading }}
          enableColumnFilters={false}
          enableGlobalFilter={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableHiding={false}
          enableColumnActions={false}
          enablePagination={false}
          enableBottomToolbar={false}
          muiTableProps={{
            sx: {
              "& .MuiTableHead-root": {
                "& .MuiTableCell-root": {
                  backgroundColor: "#f8f9fa",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: "#333",
                  borderBottom: "2px solid #e0e0e0",
                },
              },
              "& .MuiTableBody-root": {
                "& .MuiTableRow-root": {
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                },
              },
              boxShadow: "none",
              elevation: 0,
            },
          }}
          muiTablePaperProps={{
            sx: {
              boxShadow: "none",
              elevation: 0,
              border: "none",
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => {
              if (onPaymentProfileSelect) {
                onPaymentProfileSelect(row.original);
              }
            },
            sx: {
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                "& .MuiTableCell-root": {
                  backgroundColor: "transparent",
                },
              },
              "&:active": {
                backgroundColor: "#e3f2fd",
              },
            },
          })}
        />
      </Paper>

      {/* Thông tin tóm tắt hợp đồng và thanh toán */}
      <Paper
        sx={{
          mt: 3,
          p: 3,
          border: "1px solid #e0e0e0",
          boxShadow: "none",
          elevation: 0,
          backgroundColor: "#f8f9fa",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            mb: 2,
            color: "#333",
          }}
        >
          Tóm tắt tài chính
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
          }}
        >
          {/* Thông tin hợp đồng */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: "#1976d2",
                borderBottom: "1px solid #e0e0e0",
                pb: 1,
              }}
            >
              Giá trị hợp đồng
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Trước thuế:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrencyWithUnit(getContractTotalBeforeTax())}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Sau thuế ({contractData?.vat || 8}% VAT):
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#1976d2" }}
                >
                  {formatCurrencyWithUnit(getContractTotalAfterTax())}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Thông tin đã thanh toán */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: "#4caf50",
                borderBottom: "1px solid #e0e0e0",
                pb: 1,
              }}
            >
              Đã thanh toán
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Trước thuế:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrencyWithUnit(getTotalPaidBeforeTax())}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Sau thuế:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#4caf50" }}
                >
                  {formatCurrencyWithUnit(getTotalPaidAfterTax())}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Thông tin còn lại */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: "#ff9800",
                borderBottom: "1px solid #e0e0e0",
                pb: 1,
              }}
            >
              Còn lại
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Trước thuế:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrencyWithUnit(
                    getContractTotalBeforeTax() - getTotalPaidBeforeTax()
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Sau thuế:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#ff9800" }}
                >
                  {formatCurrencyWithUnit(
                    getContractTotalAfterTax() - getTotalPaidAfterTax()
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Thanh tiến độ thanh toán */}
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Tiến độ thanh toán
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {(() => {
                const totalAfterTax = getContractTotalAfterTax();
                const paidAfterTax = getTotalPaidAfterTax();

                if (totalAfterTax === 0) {
                  return "0.0%";
                }

                const percentage = (paidAfterTax / totalAfterTax) * 100;
                return isFinite(percentage)
                  ? percentage.toFixed(1) + "%"
                  : "0.0%";
              })()}
            </Typography>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: 8,
              backgroundColor: "#e0e0e0",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${(() => {
                  const totalAfterTax = getContractTotalAfterTax();
                  const paidAfterTax = getTotalPaidAfterTax();

                  if (totalAfterTax === 0) {
                    return 0;
                  }

                  const percentage = (paidAfterTax / totalAfterTax) * 100;
                  return Math.min(isFinite(percentage) ? percentage : 0, 100);
                })()}%`,
                height: "100%",
                backgroundColor: "#4caf50",
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Menu Actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            boxShadow: "none",
            border: "1px solid #e0e0e0",
            elevation: 0,
          },
        }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1, fontSize: "1.125rem" }} />
          Chỉnh sửa
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "#d32f2f" }}>
          <DeleteIcon sx={{ mr: 1, fontSize: "1.125rem" }} />
          Xóa
        </MenuItem>
      </Menu>

      {/* Payment Profile Modal */}
      <PaymentProfileModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingProfile(null);
        }}
        onSave={handleSaveProfile}
        contractId={contractId}
        contractNumber={contractNumber}
        contractName={contractName}
        editingProfile={editingProfile}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            color: "#333",
            pb: 2,
          }}
        >
          Xác nhận xóa
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography sx={{ fontFamily: "Inter, sans-serif", color: "#666" }}>
            Bạn có chắc chắn muốn xóa kỳ thanh toán "
            {profileToDelete?.paymentName}" không?
            <br />
            Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDelete}
            sx={{
              fontFamily: "Inter, sans-serif",
              textTransform: "none",
              color: "#666",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              px: 3,
              "&:hover": {
                backgroundColor: "#f5f5f5",
                border: "1px solid #ccc",
              },
            }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              fontFamily: "Inter, sans-serif",
              textTransform: "none",
              backgroundColor: "#d32f2f",
              color: "white",
              borderRadius: 1,
              px: 3,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#b71c1c",
                boxShadow: "none",
              },
            }}
          >
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ fontFamily: "Inter, sans-serif" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Permission Snackbar */}
      <Snackbar
        open={permissionSnackbar.open}
        autoHideDuration={3500}
        onClose={() =>
          setPermissionSnackbar({ ...permissionSnackbar, open: false })
        }
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            setPermissionSnackbar({ ...permissionSnackbar, open: false })
          }
          severity={permissionSnackbar.severity}
          sx={{ width: "100%" }}
        >
          {permissionSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentProfileTable;
